/**
 * Herz zum Vormerken eines Rezepts.
 *
 * Die Merkliste ist unabhängig von den Sternen: Ein Rezept kann vorgemerkt
 * sein, ohne bewertet zu sein.
 */
import { useState } from 'react';
import { ApiError } from '../api/client.ts';
import { sendOrQueue } from '../state/outbox.ts';
import { AppIcon } from './AppIcon.tsx';
import { texts } from '../texts/de.ts';

/** Eigenschaften des Herzens. */
export interface FavoriteButtonProps {
  readonly recipeId: string;
  /** Titel für die Anzeige wartender Änderungen. */
  readonly title?: string;
  readonly favorite: boolean;
  readonly onChange: (favorite: boolean) => void;
  readonly size?: number;
  readonly className?: string;
}

/** Ein Herz, das die Merkliste umschaltet. */
export function FavoriteButton({
  recipeId,
  title = 'Rezept',
  favorite,
  onChange,
  size = 20,
  className = '',
}: FavoriteButtonProps) {
  const [busy, setBusy] = useState(false);

  /** Schaltet die Markierung um. */
  async function toggle() {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      // Ohne Netz wird die Änderung gemerkt und später übertragen.
      await sendOrQueue({
        key: `favorite:${recipeId}`,
        method: 'PUT',
        path: `/api/recipes/${recipeId}/favorite`,
        body: { favorite: !favorite },
        label: `${favorite ? texts.favorites.remove : texts.favorites.add}: ${title}`,
      });
      onChange(!favorite);
    } catch (caught) {
      if (caught instanceof ApiError) {
        window.alert(caught.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        // Auf Rezeptkarten liegt das Herz in einem Verweis: nicht mitnavigieren.
        event.preventDefault();
        event.stopPropagation();
        void toggle();
      }}
      aria-pressed={favorite}
      aria-label={favorite ? texts.favorites.remove : texts.favorites.add}
      title={favorite ? texts.favorites.remove : texts.favorites.add}
      className={`rounded-lg p-1 transition-colors ${className} ${
        favorite ? 'text-accent' : 'text-content-muted hover:text-accent'
      }`}
    >
      <AppIcon name="heart" size={size} />
    </button>
  );
}
